import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useOutlet,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "./OrdersDetails.scss";
import Home from "../Home/Home";
import OrdersDetailsDashboard from "./OrdersDetailsDashboard";
import { Sparks } from "../../assets/Icons/Sparks";
import {
  fetchOrderById,
  fetchActionRequiredLogs,
  fetchOpenOrdersSummaries,
  clearCurrentOrder,
} from "../../store/slices/ordersSlice";
import { setAllowedOrderIds } from "../../store/slices/userSlice";
import { LoadingIndicator } from "../CommonUIComponents/LoadingIndicator/LoadingIndicator";
import { OrdersService } from "../../api/services/orders.service";
import { FilesService } from "../../api/services/files.service";

const DEFAULT_DATA_RECEIVED_FLOW_STATE = {
  fileValidationStatus: "idle",
  fileMappingStatus: "idle",
  dataValidationStatus: "idle",
};

// AI message for attach files instructions
const ATTACH_FILES_AI_MESSAGE = `Please attach following files from the left panel by clicking "Attach files" button

▸ Journal Entry (JE) Files (Mandatory)
▸ Trial Balance (TB) Files (Mandatory)
▸ Chart of Account (COA) Files (Recommended)

Before you attach..

▸ Ensure files are unzipped.
▸ Upload files in the correct folders
▸ Confirm all mandatory files are placed in the mandatory (CY) folder

Once you're done, just let me know—I'll take it from there and start pre-validating the files for you.`;

const normalizeCategory = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const FILES_STATUS_REFRESH_TEXT =
  "You can refresh the screen for quick updates.";

// Picks the chat paragraphs for already attached files; null when no message applies
const getFilesStatusParagraphs = (files) => {
  const statuses = files.map((file) => normalizeCategory(file?.status));
  if (
    statuses.some((status) =>
      ["attached", "extracting headers", "mapping underway"].some((s) =>
        status.includes(s),
      ),
    )
  ) {
    return [
      "Please wait while headers are extracted and mapped from the uploaded files.",
      FILES_STATUS_REFRESH_TEXT,
    ];
  }
  if (statuses.some((status) => status.includes("headers mapped"))) {
    return [
      "Please review and confirm the mapping of standard files in order to run data triage. Once confirmed, data triage will automatically run on the confirmed files.",
      FILES_STATUS_REFRESH_TEXT,
    ];
  }
  if (statuses.some((status) => status === "mapped")) {
    return [
      "Please wait while mapped files are initiated for data triage.",
      FILES_STATUS_REFRESH_TEXT,
    ];
  }
  return null;
};

const getSafeHttpUrl = (value) => {
  try {
    const url = new URL(String(value || ""));
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.href
      : "";
  } catch {
    return "";
  }
};

// Main container component for order details page with AI assistant panel
const OrdersDetailsHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { orderId } = useParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [isRightSectionOpen, setIsRightSectionOpen] = useState(true);
  const [isChatFullScreen, setIsChatFullScreen] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [dataReceivedFlowState, setDataReceivedFlowState] = useState(
    DEFAULT_DATA_RECEIVED_FLOW_STATE,
  );
  const [requestedMainTab, setRequestedMainTab] = useState(null);
  // Monotonically increasing counter. Bumped after successful updateOrder calls
  // (via handleRefreshOrderDetails) so the Chatbot can refresh its update-trail logs.
  const [trailRefreshKey, setTrailRefreshKey] = useState(0);
  const [attachFilesMessages, setAttachFilesMessages] = useState(null);
  const [incompleteDataMessage, setIncompleteDataMessage] = useState(null);
  const [isAILoading, setIsAILoading] = useState(false);
  const [independenceUrl, setIndependenceUrl] = useState(null);

  // Redux store selectors for order data and action required logs
  const {
    currentOrder,
    isLoading,
    actionRequiredLogs,
    openOrdersSummaries,
    isLoadingLogs,
  } = useSelector((state) => state.orders);
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  console.log("Selected Role:", selectedRole);
  const rolesLoaded = useSelector((state) => state.user?.rolesLoaded);

  const notificationNavigationId = location.state?.notificationNavigationId;
  const superAgentNavigationId = location.state?.superAgentNavigationId;
  const recentOrderNavigationId = location.state?.recentOrderNavigationId;
  // Combine all navigation IDs - any one should trigger a fresh fetch
  const combinedNavigationId =
    notificationNavigationId ||
    superAgentNavigationId ||
    recentOrderNavigationId ||
    null;

  const currentUserRole = useMemo(() => {
    const roleSource = selectedRole?.roleName || "";

    return String(roleSource).trim();
  }, [selectedRole]);

  const normalizedCurrentUserRole = useMemo(
    () => currentUserRole.toUpperCase(),
    [currentUserRole],
  );

  const isRegionalLead = useMemo(
    () => normalizedCurrentUserRole === "REGIONAL LEAD",
    [normalizedCurrentUserRole],
  );

  useEffect(() => {
    if (location.state?.returnToFiles) {
      setRequestedMainTab("Files");
    }
  }, [location.key, location.state?.returnToFiles]);

  // Clear previous order data and fetch new order data when orderId or navigation ID changes
  // Wait for roles to be loaded before fetching to ensure roleId is available
  useEffect(() => {
    if (!orderId || !rolesLoaded) return;

    // Clear previous order state to prevent showing stale summaries
    dispatch(clearCurrentOrder());
    dispatch(fetchOrderById({ orderId, roleId: selectedRole?.roleId || "" }));
    setIndependenceUrl(null);
    let isCurrent = true;
    OrdersService.getIndependenceStatus(orderId)
      .then((response) => {
        const result = response?.data ?? response;
        const status = String(result?.status || "")
          .trim()
          .toLowerCase();
        if (isCurrent && status === "new") {
          setIndependenceUrl(result?.engagementIndependenceURL || "");
        }
      })
      .catch((error) => {
        console.warn("Failed to fetch independence status:", error);
      });
    setDataReceivedFlowState(DEFAULT_DATA_RECEIVED_FLOW_STATE);
    return () => {
      isCurrent = false;
    };
  }, [
    orderId,
    dispatch,
    selectedRole?.roleId,
    combinedNavigationId,
    rolesLoaded,
  ]);

  // Fetch logs/summaries when currentOrder is available with axonId
  // Check actionNeeded from headerSection to determine which API to call
  // Extract stable primitive values to avoid re-running effect on location.state object reference changes
  const locationSource = location.state?.source;
  const locationCategory = location.state?.notificationCategory;

  useEffect(() => {
    const headerSection = currentOrder?.[0]?.headerSection?.[0];
    const axonId = headerSection?.axonId;

    if (!axonId) {
      return;
    }

    const isActionNeededNotification =
      locationSource != null
        ? locationSource === "actionNeeded"
        : normalizeCategory(locationCategory) === "action needed";
    const isActionNeeded =
      isActionNeededNotification || headerSection?.actionNeeded === true;

    const forceLogsRefresh = Boolean(combinedNavigationId);

    // Call different API based on actionNeeded precedence from navigation state or headerSection.
    if (isActionNeeded) {
      dispatch(fetchActionRequiredLogs({ axonId, force: forceLogsRefresh }));
    } else {
      dispatch(fetchOpenOrdersSummaries({ axonId, force: forceLogsRefresh }));
    }
  }, [
    currentOrder,
    dispatch,
    combinedNavigationId,
    locationSource,
    locationCategory,
  ]);

  // Updates local state when Redux currentOrder changes
  useEffect(() => {
    if (currentOrder) {
      setOrderDetails(currentOrder);
      // Safely access nested properties with optional chaining
      const status = Array.isArray(currentOrder)
        ? currentOrder[0]?.headerSection?.[0]?.currentSubStage
        : currentOrder?.headerSection?.[0]?.currentSubStage ||
          currentOrder?.status;
      if (status) {
        setCurrentStatus(status);
      }
    } else {
      // Clear order details when currentOrder is cleared to prevent stale data
      // This ensures Chatbot receives null orderDetails when switching orders
      setOrderDetails(null);
    }
  }, [currentOrder]);

  // Navigates back to home page
  const handleBackClick = () => {
    // Clear current order state when navigating away to prevent stale data issues
    dispatch(clearCurrentOrder());
    navigate("/home");
  };

  // Toggles fullscreen mode for chat section
  const handleToggleFullScreen = () => {
    setIsChatFullScreen((prev) => !prev);
  };

  // Collapses the right AI assistant section (close button)
  // If in fullscreen, exit fullscreen and show dashboard in fullscreen mode
  const handleClose = () => {
    if (isChatFullScreen) {
      setIsChatFullScreen(false);
      setIsRightSectionOpen(false);
    } else {
      setIsRightSectionOpen(false);
    }
  };

  // Expands the right AI assistant section
  const handleExpandClick = () => {
    setIsRightSectionOpen(true);
    setIsChatFullScreen(false);
  };

  // Handles action button clicks from AI assistant
  const handleActionClick = (action) => {
    // Handle any AI assistant actions if needed
    const actionType =
      typeof action === "object" && action?.type ? String(action.type) : "";
    const actionText =
      typeof action === "object"
        ? action?.action || action?.label || ""
        : String(action || "");

    if (
      actionType === "frontendAttachFile" ||
      actionText === "Attach Files" ||
      actionText === "Attach File"
    ) {
      setRequestedMainTab("Files");
    }
  };

  // Handles Files tab click from OrdersDetailsDashboard
  const handleFilesTabClick = async () => {
    // Chat guidance only applies while the order is in Data Received
    const headerData = Array.isArray(currentOrder)
      ? currentOrder[0]?.headerSection?.[0]
      : currentOrder?.headerSection?.[0];
    const status = headerData?.currentSubStage || headerData?.status || "";
    if (normalizeCategory(status) !== "data received") {
      return;
    }

    let attachedFiles = [];
    if (headerData?.requestId) {
      try {
        const response = await FilesService.getALLFiles(headerData.requestId);
        const rawFiles =
          response?.data?.files ||
          response?.files ||
          response?.data ||
          response;
        attachedFiles = (Array.isArray(rawFiles) ? rawFiles : []).filter(
          (file) => normalizeCategory(file?.status) !== "replaced",
        );
      } catch (error) {
        console.warn("Failed to fetch files for chat message:", error);
      }
    }

    let statusParagraphs = null;
    if (attachedFiles.length > 0) {
      statusParagraphs = getFilesStatusParagraphs(attachedFiles);
      if (!statusParagraphs) return;
    }

    // Create messages to be added to chat
    const timestamp = Date.now();
    const userMessage = {
      id: `m${timestamp}`,
      role: "user",
      text: "Attach Files",
      name: "You",
      timestamp: timestamp,
    };
    const aiMessage = {
      id: `m${timestamp + 1}`,
      role: "ai",
      type: "aiCard",
      attachFilesCard: true,
      text: ATTACH_FILES_AI_MESSAGE,
      ...(statusParagraphs ? { statusParagraphs } : {}),
      header: { title: "GDS.ai Axon" },
      timestamp: timestamp + 1,
    };
    setAttachFilesMessages({ userMessage, aiMessage });
  };

  // Refreshes order details in the left section and resets to Order Info tab
  // Scenario 4: Fetch AI orders when refreshing order details page after chat update
  const handleRefreshOrderDetails = useCallback(async () => {
    if (orderId) {
      dispatch(fetchOrderById({ orderId, roleId: selectedRole?.roleId || "" }));
      // Reset to Order Info tab when refreshing
      setRequestedMainTab("Order Info");
      // Trigger an update-trail refresh in the chat section
      setTrailRefreshKey((k) => k + 1);

      // Fetch AI-allowed order IDs when refreshing order details
      if (selectedRole?.roleId) {
        try {
          const aiOrdersData = await OrdersService.getAIOrders(
            selectedRole.roleId,
          );
          const rawOrderIds = Array.isArray(aiOrdersData)
            ? aiOrdersData
            : aiOrdersData?.data?.orderIds || aiOrdersData?.orderIds || [];
          const orderIds = rawOrderIds.map((id) => String(id));
          dispatch(setAllowedOrderIds(orderIds));
        } catch (aiErr) {
          console.warn("Failed to fetch AI order IDs:", aiErr);
        }
      }
    }
  }, [orderId, dispatch, selectedRole?.roleId]);

  // Memoize to prevent infinite re-renders in child components that depend on this callback
  const handleDataReceivedFlowChange = useCallback((nextState) => {
    setDataReceivedFlowState((prevState) => ({
      ...prevState,
      ...nextState,
    }));
  }, []);

  // Handles incomplete data button click from ReviewMappingsModal
  // Sets the message to be sent to the chatbot
  const handleIncompleteData = useCallback((message) => {
    setIncompleteDataMessage(message);
  }, []);

  const nestedRoute = useOutlet({ onIncompleteData: handleIncompleteData });

  return (
    <>
      <div
        className={`orders-home-container orders-details ${
          !isRightSectionOpen ? "right-section-collapsed" : ""
        } ${isChatFullScreen ? "chat-fullscreen" : ""}`}
        style={nestedRoute ? { display: "none" } : undefined}
        aria-hidden={nestedRoute ? "true" : undefined}
      >
        {!isChatFullScreen && (
          <div className="orders-left-section">
            <LoadingIndicator show={isLoading} fullscreen={false}>
              <OrdersDetailsDashboard
                orderId={orderId}
                orderDetails={orderDetails}
                isLoading={isLoading}
                actionRequiredLogs={actionRequiredLogs}
                isLoadingLogs={isLoadingLogs}
                onRefreshOrderDetails={handleRefreshOrderDetails}
                onDataReceivedFlowChange={handleDataReceivedFlowChange}
                onFilesTabClick={handleFilesTabClick}
                requestedMainTab={requestedMainTab}
                isAILoading={isAILoading}
                onIncompleteData={handleIncompleteData}
              />
            </LoadingIndicator>
          </div>
        )}
        {isRightSectionOpen && (
          <div
            className={`orders-right-section ${isChatFullScreen ? "fullscreen" : ""}`}
          >
            <Home
              showBackButton={true}
              onBackClick={handleBackClick}
              onClose={handleClose}
              onToggleFullScreen={handleToggleFullScreen}
              isChatFullScreen={isChatFullScreen}
              orderId={orderId}
              orderDetails={orderDetails}
              onActionClick={handleActionClick}
              onRefreshOrderDetails={handleRefreshOrderDetails}
              dataReceivedFlowState={dataReceivedFlowState}
              attachFilesMessages={attachFilesMessages}
              incompleteDataMessage={incompleteDataMessage}
              onAILoadingChange={setIsAILoading}
              trailRefreshKey={trailRefreshKey}
              notificationNavigationId={combinedNavigationId}
            />
          </div>
        )}
        {!isRightSectionOpen && (
          <div className="expand-icon-container" onClick={handleExpandClick}>
            <Sparks width="24" height="24" color="#000" />
          </div>
        )}
      </div>
      {nestedRoute && <div className="orders-nested-route">{nestedRoute}</div>}
      {independenceUrl !== null && !isRegionalLead && (
        <div className="independence-popup-backdrop" role="presentation">
          <div
            className="independence-popup"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="independence-popup-title"
          >
            <h3 id="independence-popup-title">
              Independence confirmation required
            </h3>
            <p>
              Please complete the engagement independence confirmation before
              working on this order.
            </p>
            {getSafeHttpUrl(independenceUrl) && (
              <a
                href={getSafeHttpUrl(independenceUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Go to engagement independence
              </a>
            )}
            <div className="independence-popup-actions">
              <button type="button" onClick={() => navigate("/orders")}>
                Close Order
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrdersDetailsHome;
